import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type { PlayerAction } from '../api/client';
import { useGrantCredits, useHasRole, usePlayer, usePlayerAction } from '../api/hooks';
import type { AdminRole, MemberTier, PlayerDetail } from '../api/types';
import { PageHeader } from '../app/Shell';
import { CHAPTER_STATUS, LEDGER_KIND, REPORT_REASON, REPORT_STATE, ROLE_LABEL, STAGE_LABEL, TIER_LABEL, USER_STATUS } from '../app/labels';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  ErrorNote,
  Field,
  Id,
  KeyValues,
  Menu,
  QueryView,
  ReasonField,
  ScoreBar,
  Segmented,
  Tabs,
  When,
  fmtDateTime,
  fmtInt,
  fmtSigned,
  fmtUsd,
  reasonError,
  toast,
  type MenuItem,
} from '../ui';
import s from './UserDetail.module.css';

const TABS = [
  { value: 'overview', label: '概覽' },
  { value: 'wallet', label: '錢包' },
  { value: 'purchases', label: '購買' },
  { value: 'story', label: '故事' },
  { value: 'manga', label: '漫畫' },
  { value: 'reports', label: '檢舉' },
  { value: 'devices', label: '裝置' },
] as const;
type Tab = (typeof TABS)[number]['value'];

/** One player's account: a header with the key numbers and actions, then tabs. */
export function UserDetailPage() {
  const { id = '' } = useParams();
  const player = usePlayer(id);
  const name = player.data ? (player.data.character?.name ?? player.data.user.displayName ?? player.data.user.email ?? '訪客') : '…';
  return (
    <>
      <PageHeader
        title="玩家檔案"
        crumbs={
          <>
            <Link to="/users">玩家</Link> › {name}
          </>
        }
      />
      <QueryView query={player} loading="載入帳戶…">
        {(p) => <Body p={p} />}
      </QueryView>
    </>
  );
}

const yesNo = (v: boolean | undefined) => (v === undefined ? '—' : v ? '開' : '關');
const PAGE = 15;

function Body({ p }: { p: PlayerDetail }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.some((t) => t.value === raw) ? (raw as Tab) : 'overview';
  const [action, setAction] = useState<ActionKey | null>(null);
  const counts: Partial<Record<Tab, number>> = {
    wallet: p.ledger.length,
    purchases: p.entitlements.length,
    story: p.personalChapters.length,
    manga: p.renders.length,
    reports: p.reports.against.length + p.reports.filed.length,
    devices: p.devices.length,
  };
  return (
    <>
      <Header p={p} onAction={setAction} />
      <Tabs
        label="帳戶分頁"
        value={tab}
        onChange={(v) => setParams(v === 'overview' ? {} : { tab: v }, { replace: true })}
        tabs={TABS.map((t) => ({ value: t.value, label: counts[t.value] ? <>{t.label} <span className="faint small">{counts[t.value]}</span></> : t.label }))}
      />
      {tab === 'overview' && <Overview p={p} />}
      {tab === 'wallet' && <Wallet p={p} />}
      {tab === 'purchases' && <Purchases p={p} />}
      {tab === 'story' && <Stories p={p} />}
      {tab === 'manga' && <Manga p={p} />}
      {tab === 'reports' && <Reports p={p} />}
      {tab === 'devices' && <Devices p={p} />}
      {action && <ActionDialog key={action} action={action} p={p} onClose={() => setAction(null)} />}
    </>
  );
}

// ---------- header ----------

function Header({ p, onAction }: { p: PlayerDetail; onAction: (a: ActionKey) => void }) {
  const u = p.user;
  const st = USER_STATUS[u.status];
  const roles: Record<AdminRole, boolean> = { viewer: true, support: useHasRole('support'), moderator: useHasRole('moderator'), content: useHasRole('content'), owner: useHasRole('owner') };
  const closed = u.status === 'deleting' || u.status === 'deleted';
  const can = (a: ActionKey) => !closed && roles[ACTIONS[a].role];
  const why = (a: ActionKey) => (closed ? '帳戶正在刪除' : roles[ACTIONS[a].role] ? undefined : `需要${ROLE_LABEL[ACTIONS[a].role]}角色`);
  const btn = (a: ActionKey, variant: 'primary' | 'default' | 'danger' = 'default') => (
    <Button variant={variant} disabled={!can(a)} title={why(a)} onClick={() => onAction(a)}>
      {ACTIONS[a].label}
    </Button>
  );
  const more: MenuItem[] = (['coins', 'resetDaily', 'revokeSub', 'revokePass', 'clearPaymentHold', 'export', 'ban', 'delete'] as ActionKey[])
    .filter((a) => (a === 'revokeSub' ? p.membership.sub?.source === 'admin' : a === 'revokePass' ? p.membership.pass?.source === 'admin' : a === 'clearPaymentHold' ? !!u.paymentHoldAt : a === 'ban' ? u.status === 'active' : true))
    .map((a) => ({ label: ACTIONS[a].label, onClick: () => onAction(a), disabled: !can(a), title: why(a), danger: ACTIONS[a].tone === 'danger', separated: a === 'ban' || (a === 'delete' && u.status !== 'active') }));
  const today = new Date().toISOString().slice(0, 10);
  const membershipEnd = p.membership.sub ?? p.membership.pass;

  return (
    <section className={s.header}>
      <Avatar url={p.avatarUrl} name={p.character?.name ?? u.email ?? '?'} size={104} />
      <div className={s.who}>
        <div className="row" style={{ gap: 'var(--sp-3)' }}>
          <h2 className={s.name}>{p.character?.name ?? u.displayName ?? '（未建立角色）'}</h2>
          <span className="row">
            <Badge tone={st.tone}>{st.label}</Badge>
            {p.membership.tier !== 'free' && <Badge tone="accent">{TIER_NAME[p.membership.tier]}</Badge>}
            {u.isGuest && <Badge>訪客</Badge>}
            {u.adminRoles.length > 0 && <Badge tone="info">後台：{u.adminRoles.map((r) => ROLE_LABEL[r]).join('、')}</Badge>}
          </span>
        </div>
        <div className={s.facts}>
          <span>{u.email ?? <span className="faint">未綁定電郵</span>}</span>
          <span>
            玩家 ID <Id value={u.publicId} full />
          </span>
          <span>
            ID <Id value={u._id} />
          </span>
          <span>
            {u.preferences.city ?? '—'} · {u.preferences.locale}
          </span>
          <span>
            註冊 <When at={u.createdAt} /> · 最後活動 <When at={u.lastActiveAt} />
          </span>
        </div>
      </div>
      <div className={s.numbers}>
        <div>
          <span>章節券</span>
          <strong className="num pos">{fmtInt(p.wallet?.credits ?? 0)}</strong>
          {(p.wallet?.reserved ?? 0) > 0 && <small>預留 {fmtInt(p.wallet!.reserved)}</small>}
        </div>
        <div>
          <span>墨幣</span>
          <strong className="num pos">{fmtInt(p.save?.coins ?? 0)}</strong>
        </div>
        <div>
          <span>{membershipEnd && p.membership.tier !== 'free' ? `${TIER_NAME[p.membership.tier]}至` : '會籍'}</span>
          <strong className="num" style={{ color: membershipEnd ? 'var(--accent-deep)' : 'var(--text-3)' }}>
            {membershipEnd ? (membershipEnd.expiresAt ? membershipEnd.expiresAt.slice(0, 10) : '不限期') : '—'}
          </strong>
        </div>
        <div>
          <span>今日 AI 章節</span>
          <strong className="num">{u.aiChapters?.day === today ? u.aiChapters.count : 0}</strong>
        </div>
      </div>
      <div className={s.actions}>
        {btn('credits', 'primary')}
        {btn('membership')}
        {btn('notify')}
        <Menu label="更多" items={more} />
        <span className={s.grow} />
        {u.status === 'suspended' ? btn('unban') : btn('suspend', 'danger')}
      </div>
    </section>
  );
}

// ---------- tabs ----------

type Rel = NonNullable<PlayerDetail['save']>['relationships'][number];

function Overview({ p }: { p: PlayerDetail }) {
  const u = p.user;
  const rels = [...(p.save?.relationships ?? [])].sort((a, b) => (b.dims['affection'] ?? 0) - (a.dims['affection'] ?? 0));
  return (
    <div className="split">
      <div className="stack-lg">
        <div className={s.pair}>
          <Card title="帳戶">
            <KeyValues
              items={[
                ['年齡確認', u.ageGate ? `${fmtDateTime(u.ageGate.passedAt)} · 條款 ${u.ageGate.termsVersion}` : '未完成'],
                ['體力', u.energy ? `${u.energy.value}（${fmtDateTime(new Date(u.energy.at).toISOString())} 更新）` : '滿'],
                ['新手禮物', u.starterGift === 'deferred' ? <Badge tone="warn">暫緩（同一網絡開了太多訪客帳戶，綁定登入後發放）</Badge> : u.starterGift === 'denied' ? <Badge tone="warn">不發放（這個登入方式已在另一帳戶領過）</Badge> : '已發放'],
                ['免費繪製版', u.freeIllustratedAt ? `已用（${fmtDateTime(u.freeIllustratedAt)}）` : '未用'],
                ['角色推送', yesNo(u.preferences.npcNotifications)],
                ['有效登入', `${fmtInt(p.activeSessions)} 個`],
                ['Stripe customer', u.stripeCustomerId ? <Id value={u.stripeCustomerId} /> : '—'],
                ['付款覆核', u.paymentHoldAt ? <Badge tone="warn">{`付款爭議，暫停購買（${fmtDateTime(u.paymentHoldAt)}）`}</Badge> : '—'],
              ]}
            />
          </Card>
          <Card title="角色">
            {p.character ? (
              <KeyValues
                items={[
                  ['名字', p.character.name],
                  ['性別', p.character.gender === 'male' ? '男' : '女'],
                  ['性格 / 目標', `${p.character.trait ?? '—'} · ${p.character.lifeGoal ?? '—'}`],
                  ['遊戲日', p.save?.day != null ? `第 ${p.save.day} 日` : '—'],
                  ['能力', p.save ? Object.entries(p.save.stats).map(([k, v]) => `${k} ${v}`).join(' · ') || '—' : '—'],
                ]}
              />
            ) : (
              <p className="faint">還未建立角色。</p>
            )}
          </Card>
        </div>
        <Card title="最近的 AI 章節" sub="最新 5 章" actions={<Link to="?tab=story">全部 →</Link>} flush>
          <StoryTable rows={p.personalChapters.slice(0, 5)} />
        </Card>
      </div>
      <Card title="關係" sub={`${rels.length} 位已認識`}>
        {rels.length ? (
          <div className={s.rels}>
            {rels.map((r) => (
              <RelRow key={r.npcId} r={r} />
            ))}
          </div>
        ) : (
          <p className="faint">還未認識任何人。</p>
        )}
      </Card>
    </div>
  );
}

function RelRow({ r }: { r: Rel }) {
  return (
    <div className={s.rel}>
      <Avatar url={r.portraitUrl} name={r.name} size={52} />
      <div className={s.relBody}>
        <div className="spread">
          <strong>{r.name}</strong>
          <span className="faint small">
            {STAGE_LABEL[r.stage] ?? r.stage} · 第 {r.metDay} 日認識
          </span>
        </div>
        <div className={s.relBars}>
          <span>好感</span>
          <ScoreBar value={r.dims['affection'] ?? 0} />
          <b className="num">{Math.round(r.dims['affection'] ?? 0)}</b>
          <span>信任</span>
          <ScoreBar value={r.dims['trust'] ?? 0} color="var(--info)" />
          <b className="num">{Math.round(r.dims['trust'] ?? 0)}</b>
          <span>心動</span>
          <ScoreBar value={r.dims['romanticInterest'] ?? 0} color="var(--accent-deep)" />
          <b className="num">{Math.round(r.dims['romanticInterest'] ?? 0)}</b>
        </div>
      </div>
    </div>
  );
}

function Wallet({ p }: { p: PlayerDetail }) {
  return (
    <Card title="章節券記錄" sub="最新 100 筆" flush>
      <DataTable
        rows={p.ledger}
        rowKey={(r) => r._id}
        pageSize={PAGE}
        empty="沒有章節券記錄。"
        emptyHint="玩家領取歡迎禮物、購買或使用章節券後會出現在這裏。"
        columns={[
          { key: 'at', header: '時間', render: (r) => <When at={r.createdAt} />, width: 120 },
          { key: 'kind', header: '類型', render: (r) => <Badge tone={r.kind === 'purchase' || r.kind === 'grant' || r.kind === 'reward' ? 'ok' : r.kind === 'adjust' ? 'accent' : r.kind === 'refund' ? 'danger' : 'neutral'}>{LEDGER_KIND[r.kind]}</Badge>, width: 110 },
          { key: 'amount', header: '章節券', align: 'right', render: (r) => <span className={`num ${r.amount > 0 ? 'pos' : r.amount < 0 ? 'neg' : 'faint'}`} style={{ fontSize: 16 }}>{fmtSigned(r.amount)}</span>, width: 90 },
          { key: 'reason', header: '原因', render: (r) => r.reason || <span className="faint">—</span> },
          { key: 'ref', header: '參考', render: (r) => <Id value={r.globalKey ?? r.operationKey} /> },
          { key: 'env', header: '', render: (r) => (r.environment === 'sandbox' ? <Badge>sandbox</Badge> : null), width: 80 },
        ]}
      />
      <p className="small faint" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
        退款：請在 Stripe Dashboard 退款。全額退款或付款爭議（chargeback）會自動扣回全部章節券（已用的變成負數）、該商品的墨幣和禮包服裝；爭議另會暫停該帳戶購買，直至解除付款覆核。
      </p>
    </Card>
  );
}

function Purchases({ p }: { p: PlayerDetail }) {
  return (
    <Card title="購買與會籍" flush>
      <DataTable
        rows={p.entitlements}
        rowKey={(r) => r._id}
        pageSize={PAGE}
        empty="沒有購買或會籍。"
        emptyHint="Plus、月卡及後台贈送的會籍會列在這裏。"
        columns={[
          { key: 'key', header: '項目', render: (r) => <code>{r.key}</code> },
          { key: 'source', header: '來源', render: (r) => r.source },
          { key: 'status', header: '狀態', render: (r) => <Badge tone={r.status === 'active' ? 'ok' : r.status === 'revoked' ? 'danger' : 'neutral'}>{r.status}</Badge> },
          { key: 'product', header: '產品', render: (r) => (r.productId ? <code>{r.productId}</code> : '—') },
          { key: 'exp', header: '到期', render: (r) => (r.expiresAt ? fmtDateTime(r.expiresAt) : '—') },
          { key: 'at', header: '開始', render: (r) => <When at={r.createdAt} /> },
        ]}
      />
    </Card>
  );
}

function StoryTable({ rows, paged = false }: { rows: PlayerDetail['personalChapters']; paged?: boolean }) {
  return (
    <DataTable
      rows={rows}
      rowKey={(r) => r._id}
      {...(paged ? { pageSize: PAGE } : {})}
      empty="還沒有 AI 章節。"
      emptyHint="玩家完成開場後，每一章都會由 AI 撰寫並列在這裏。"
      columns={[
        { key: 'seq', header: '#', align: 'right', render: (r) => r.seq, width: 50 },
        { key: 'lane', header: '線', render: (r) => <code>{r.lane}</code> },
        { key: 'status', header: '狀態', render: (r) => <Badge tone={CHAPTER_STATUS[r.status].tone}>{CHAPTER_STATUS[r.status].label}</Badge> },
        { key: 'beat', header: '情緒節拍', render: (r) => r.emotionalBeat ?? <span className="faint">—</span> },
        { key: 'issues', header: '問題', render: (r) => (r.status === 'failed' ? <Badge tone="danger">失敗</Badge> : r.reviewIssues?.length ? <Badge tone="warn">{r.reviewIssues.length} 項</Badge> : <span className="faint">—</span>) },
        { key: 'at', header: '更新', render: (r) => <When at={r.updatedAt} /> },
        { key: 'open', header: '', render: (r) => <Link to={`/story?chapter=${r._id}`}>閱讀 →</Link>, align: 'right' },
      ]}
    />
  );
}

function Stories({ p }: { p: PlayerDetail }) {
  return (
    <Card title="AI 章節" sub="最新 30 章" actions={<Link to={`/story?user=${p.user._id}`}>在故事 QA 查看 →</Link>} flush>
      <StoryTable rows={p.personalChapters} paged />
    </Card>
  );
}

function Manga({ p }: { p: PlayerDetail }) {
  return (
    <Card title="漫畫" sub={`${p.chapters.length} 章 · ${p.renders.length} 次繪製`} flush>
      <DataTable
        rows={p.renders}
        rowKey={(r) => r._id}
        pageSize={PAGE}
        empty="還沒有漫畫。"
        emptyHint="玩家玩完一章後，漫畫會自動繪製。"
        columns={[
          { key: 'title', header: '章節', render: (r) => p.chapters.find((c) => c._id === r.chapterId)?.title ?? <Id value={r.chapterId} /> },
          { key: 'tier', header: '版本', render: (r) => <Badge tone={r.tier === 'illustrated' ? 'accent' : 'neutral'}>{TIER_LABEL[r.tier]}</Badge> },
          { key: 'v', header: 'v', align: 'right', render: (r) => r.renderVersion },
          { key: 'pages', header: 'AI 畫 / 總頁', align: 'right', render: (r) => (r.illustration ? `${r.illustration.pagesDrawn} / ${r.illustration.pagesTotal}` : '—') },
          { key: 'cost', header: '成本', align: 'right', render: (r) => fmtUsd(r.costMicrousd / 1e6) },
          { key: 'at', header: '時間', render: (r) => <When at={r.createdAt} /> },
          { key: 'open', header: '', align: 'right', render: (r) => <Link to={`/manga?render=${r._id}`}>查看 →</Link> },
        ]}
      />
    </Card>
  );
}

function Reports({ p }: { p: PlayerDetail }) {
  const rows = [...p.reports.against.map((r) => ({ ...r, dir: '被檢舉' })), ...p.reports.filed.map((r) => ({ ...r, dir: '提出' }))];
  return (
    <Card title="檢舉" sub={`被檢舉 ${p.reports.against.length} · 提出 ${p.reports.filed.length}`} flush>
      <DataTable
        rows={rows}
        rowKey={(r) => `${r.dir}:${r._id}`}
        pageSize={PAGE}
        empty="沒有檢舉。"
        emptyHint="這位玩家沒有被檢舉，也沒有檢舉別人。"
        columns={[
          { key: 'dir', header: '', render: (r) => <Badge tone={r.dir === '被檢舉' ? 'warn' : 'neutral'}>{r.dir}</Badge> },
          { key: 'reason', header: '原因', render: (r) => REPORT_REASON[r.reason] ?? r.reason },
          { key: 'state', header: '狀態', render: (r) => REPORT_STATE[r.state] },
          { key: 'note', header: '備註', render: (r) => r.note ?? <span className="faint">—</span> },
          { key: 'at', header: '時間', render: (r) => <When at={r.createdAt} /> },
        ]}
      />
    </Card>
  );
}

function Devices({ p }: { p: PlayerDetail }) {
  const rows = [
    ...p.devices.map((d) => ({ id: d._id, what: `${d.platform ?? '裝置'} 推送`, state: <Badge tone={d.enabled ? 'ok' : 'neutral'}>{d.enabled ? '啟用' : '停用'}</Badge>, at: d.updatedAt })),
    ...p.dataRequests.map((d) => ({ id: d._id, what: d.kind === 'export' ? '資料匯出' : '刪除帳戶', state: <Badge>{d.status}</Badge>, at: d.createdAt })),
  ];
  return (
    <Card title="裝置與資料請求" sub={`有效登入 ${p.activeSessions} 個`} flush>
      <DataTable
        rows={rows}
        rowKey={(r) => r.id}
        empty="沒有推送裝置或資料請求。"
        emptyHint="玩家在 App 允許通知後，裝置會出現在這裏。"
        columns={[
          { key: 'what', header: '項目', render: (r) => r.what },
          { key: 'state', header: '狀態', render: (r) => r.state },
          { key: 'at', header: '時間', render: (r) => <When at={r.at} /> },
          { key: 'id', header: 'ID', render: (r) => <Id value={r.id} /> },
        ]}
      />
    </Card>
  );
}

// ---------- actions ----------

const TIER_NAME: Record<MemberTier, string> = { pass: '月卡', plus: 'Plus', premium: 'Premium' };
const TIER_OPTIONS: ReadonlyArray<{ value: MemberTier; label: string }> = [
  { value: 'pass', label: '月卡' },
  { value: 'plus', label: 'Plus' },
  { value: 'premium', label: 'Premium' },
];

type ActionKey = 'credits' | 'coins' | 'membership' | 'revokeSub' | 'revokePass' | 'resetDaily' | 'clearPaymentHold' | 'suspend' | 'ban' | 'unban' | 'notify' | 'export' | 'delete';

interface ActionDef {
  label: string;
  role: AdminRole;
  tone?: 'danger';
  title: string;
  explain: string;
}

const ACTIONS: Record<ActionKey, ActionDef> = {
  credits: { label: '調整章節券', role: 'support', title: '調整章節券', explain: '正數加券、負數扣券（每次最多 ±1000），一張券 = 一話精繪漫畫。寫入記錄，玩家立即看到新餘額。' },
  coins: { label: '調整墨幣', role: 'support', title: '調整墨幣', explain: '直接改遊戲存檔的墨幣（不能扣到負數），玩家下次同步時看到。' },
  membership: { label: '贈送會籍', role: 'support', title: '贈送月卡 / Plus / Premium', explain: '後台贈送的會籍，玩家立即享有該級別（每日墨幣、章節及體力上限）。同一種已在生效的話延長到期日。商店訂閱進行中時不能再送 Plus / Premium。贈送不包每月章節券，需要的話另外調整章節券。' },
  revokeSub: { label: '收回贈送的 Plus / Premium', role: 'support', tone: 'danger', title: '收回贈送的訂閱', explain: '立即結束後台贈送的 Plus / Premium。商店訂閱不受影響（要在商店取消或退款）。' },
  revokePass: { label: '收回贈送的月卡', role: 'support', tone: 'danger', title: '收回贈送的月卡', explain: '立即結束後台贈送的月卡。商店購買的月卡不受影響。' },
  clearPaymentHold: { label: '解除付款覆核', role: 'support', title: '解除付款覆核', explain: '玩家曾在 Stripe 提出付款爭議（chargeback），網站已暫停這個帳戶購買。確認處理好之後解除，玩家即可再次購買。' },
  resetDaily: { label: '重設今日 AI 章節及體力', role: 'support', title: '重設今日上限', explain: '今日 AI 章節數歸零，體力回滿，玩家可以即時再玩。' },
  notify: { label: '推送訊息', role: 'support', title: '推送訊息給這位玩家', explain: '經「玩家通知」頻道推送（玩家關閉了就不會收到）。每位玩家每小時最多 3 次，發出後不能收回。' },
  suspend: { label: '停用帳戶', role: 'moderator', tone: 'danger', title: '停用帳戶', explain: '玩家會在所有裝置登出，並且不能再登入，直到解除。' },
  ban: { label: '封禁帳戶', role: 'moderator', tone: 'danger', title: '封禁帳戶', explain: '與停用相同（登出並不能登入），但審計記錄為封禁，用於嚴重違規。' },
  unban: { label: '解除停用 / 封禁', role: 'moderator', title: '恢復帳戶', explain: '帳戶回復正常，玩家可以再登入。' },
  export: { label: '匯出資料', role: 'support', title: '為玩家匯出資料', explain: '排隊產生一份個人資料匯出（與玩家在 App 內申請的相同）。' },
  delete: { label: '開始刪除帳戶', role: 'owner', tone: 'danger', title: '刪除帳戶', explain: '帳戶立即登出並進入刪除程序；完成後所有資料無法復原。' },
};

function ActionDialog({ action, p, onClose }: { action: ActionKey; p: PlayerDetail; onClose: () => void }) {
  const def = ACTIONS[action];
  const run = usePlayerAction();
  const credits = useGrantCredits();
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [days, setDays] = useState('7');
  const [tier, setTier] = useState<MemberTier>('plus');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [path, setPath] = useState('/');
  const [confirmText, setConfirmText] = useState('');

  const n = Number(amount);
  const amountOk = amount.trim() !== '' && Number.isInteger(n) && n !== 0 && (action === 'credits' ? Math.abs(n) <= 1000 : Math.abs(n) <= 100_000);
  const daysOk = Number.isInteger(Number(days)) && Number(days) >= 1 && Number(days) <= 366;
  const pushOk = title.trim().length > 0 && title.length <= 60 && body.trim().length > 0 && body.length <= 180 && path.startsWith('/');
  const deleteOk = confirmText.trim() === (p.user.email ?? p.user._id);
  const valid =
    !reasonError(reason) &&
    (action === 'credits' || action === 'coins' ? amountOk : action === 'membership' ? daysOk : action === 'notify' ? pushOk : action === 'delete' ? deleteOk : true);

  const busy = run.isPending || credits.isPending;
  const error = run.error ?? credits.error;
  const userId = p.user._id;
  const who = p.character?.name ?? p.user.email ?? '玩家';
  const done = (msg: string) => {
    toast(msg);
    onClose();
  };
  const submit = () => {
    const r = reason.trim();
    if (action === 'credits') return credits.mutate({ userId, amount: n, reason: r }, { onSuccess: () => done(`已為${who}${n > 0 ? '加' : '扣'} ${Math.abs(n)} 點`) });
    const call: { action: PlayerAction; body: Record<string, unknown>; msg: string } =
      action === 'coins' ? { action: 'coins', body: { amount: n, reason: r }, msg: `${who}的墨幣已調整 ${fmtSigned(n)}` }
      : action === 'membership' ? { action: 'membership', body: { tier, days: Number(days), reason: r }, msg: `已贈送${who} ${days} 日${TIER_NAME[tier]}` }
      : action === 'revokeSub' ? { action: 'revokeMembership', body: { kind: 'sub', reason: r }, msg: '已收回贈送的訂閱' }
      : action === 'revokePass' ? { action: 'revokeMembership', body: { kind: 'pass', reason: r }, msg: '已收回贈送的月卡' }
      : action === 'resetDaily' ? { action: 'resetDaily', body: { reason: r }, msg: '今日 AI 章節及體力已重設' }
      : action === 'clearPaymentHold' ? { action: 'clearPaymentHold', body: { reason: r }, msg: '已解除付款覆核' }
      : action === 'notify' ? { action: 'notify', body: { title: title.trim(), body: body.trim(), path, reason: r }, msg: '推送已發出' }
      : action === 'export' ? { action: 'export', body: { reason: r }, msg: '資料匯出已排隊' }
      : action === 'delete' ? { action: 'delete', body: { reason: r }, msg: '帳戶已進入刪除程序' }
      : { action: 'status', body: { action, reason: r }, msg: action === 'unban' ? '帳戶已恢復' : action === 'ban' ? '帳戶已封禁' : '帳戶已停用' };
    run.mutate({ userId, action: call.action, body: call.body }, { onSuccess: () => done(call.msg) });
  };

  let fields: ReactNode = null;
  if (action === 'credits' || action === 'coins')
    fields = (
      <Field
        label={action === 'credits' ? '章節券（正數加，負數扣）' : '墨幣（正數加，負數扣）'}
        hint={action === 'credits' ? `現有 ${fmtInt(p.wallet?.credits ?? 0)} 張${amountOk ? ` → 調整後 ${fmtInt((p.wallet?.credits ?? 0) + n)} 張` : ''}` : `現有 ${fmtInt(p.save?.coins ?? 0)} 金幣${amountOk ? ` → 調整後 ${fmtInt((p.save?.coins ?? 0) + n)}` : ''}`}
        error={amount && !amountOk ? '請輸入非零整數（章節券每次最多 ±1000）。' : null}
      >
        {(id) => <input id={id} type="number" step={1} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />}
      </Field>
    );
  if (action === 'membership')
    fields = (
      <>
        <Segmented label="會籍" value={tier} options={TIER_OPTIONS} onChange={(v) => setTier(v as MemberTier)} />
        <Field label="日數" hint={tier === 'pass' ? '1 至 366 日（月卡一般 30 日）' : '1 至 366 日'} error={!daysOk ? '請輸入 1 至 366。' : null}>
          {(id) => <input id={id} type="number" min={1} max={366} value={days} onChange={(e) => setDays(e.target.value)} autoFocus />}
        </Field>
      </>
    );
  if (action === 'notify')
    fields = (
      <>
        <Field label="標題" hint={`${title.length}/60`}>
          {(id) => <input id={id} type="text" maxLength={60} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />}
        </Field>
        <Field label="內容" hint={`${body.length}/180`}>
          {(id) => <textarea id={id} maxLength={180} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />}
        </Field>
        <Field label="打開的頁面 (path)" hint="例如 / 或 /manga">
          {(id) => <input id={id} type="text" value={path} onChange={(e) => setPath(e.target.value)} />}
        </Field>
        <PushPreview title={title} body={body} />
      </>
    );
  if (action === 'delete')
    fields = (
      <Field label={`輸入 ${p.user.email ?? p.user._id} 以確認`}>
        {(id) => <input id={id} type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus />}
      </Field>
    );

  return (
    <ConfirmDialog
      open
      title={`${def.title}：${who}`}
      tone={def.tone === 'danger' ? 'danger' : 'primary'}
      confirmLabel={def.label}
      busy={busy}
      canConfirm={valid}
      onCancel={onClose}
      onConfirm={submit}
      error={error ? <ErrorNote error={error} /> : null}
    >
      <div className="callout" data-tone={def.tone === 'danger' ? 'danger' : 'ok'}>
        <span className="callBody">{def.explain}</span>
      </div>
      {fields}
      <ReasonField value={reason} onChange={setReason} autoFocus={!fields} hint="例如客服單號及原因；會記錄在審計日誌。" />
    </ConfirmDialog>
  );
}

/** Roughly how the push lands on a lock screen. */
export function PushPreview({ title, body }: { title: string; body: string }) {
  return (
    <div className="pushPreview" aria-label="推送預覽">
      <div className="pushPreviewApp">落筆成戀 · 現在</div>
      <div className="pushPreviewTitle">{title || '標題'}</div>
      <div className="pushPreviewBody">{body || '內容'}</div>
    </div>
  );
}
